import { VariantReviewControls } from "./VariantReviewControls";
import { toRichText } from "@tldraw/tlschema";
import { PdfProperties } from "./PdfControls";
import { TraceControls } from "./TraceControls";
import { SheetProperties } from "./SheetProperties";
import { ArrangeControls } from "./ArrangeControls";
import { SourceControls } from "./SourceControls";
import { ImageControls } from "./ImageControls";
import { EditableDocumentControls, TableControls } from "./DocumentControls";
import type { WorkspaceModel } from "./useWorkspace";
import { attachToSheet } from "./sheet-operations";
import { isSource } from "./source-scale";
import { notify } from "./api";
import { selectContents } from "./sheet-selection";
import { displayObjectName } from "./object-name";
import { nativeDocument } from "../shared/document-session";
export function CompositionControls({ model }: { model: WorkspaceModel }) {
  const { editor, selected, canEdit } = model,
    s = selected.length === 1 ? selected[0] : undefined;
  const run = (fn: () => void) => {
    try {
      fn();
    } catch (e) {
      notify((e as Error).message);
    }
  };
  if (!canEdit || !selected.length || model.tool !== "select") return null;
  return (
    <div className="composition-controls">
      {s?.type === "image" &&
      s.props.assetId &&
      editor.getAsset(s.props.assetId)?.meta.pdfPages &&
      !s.meta.traceSnapshot ? (
        <PdfProperties model={model} shape={s} />
      ) : null}
      {s?.type === "sheet" && (
        <>
          <SheetProperties model={model} sheet={s} />
          <button onClick={() => selectContents(editor, s.id)}>
            Select contents
          </button>
        </>
      )}
      <ArrangeControls model={model} />
      <VariantReviewControls model={model} />
      {selected.every((s) => !["sheet", "trace", "image"].includes(s.type)) &&
        !selected.every((s) =>
          ["draw", "geo", "arrow", "highlight", "mark"].includes(s.type),
        ) && (
          <label>
            Opacity (%)
            <input
              aria-label="Selection opacity percent"
              type="number"
              min="10"
              max="100"
              step="1"
              placeholder="Mixed"
              value={
                selected.every((s) => s.opacity === selected[0].opacity)
                  ? Math.round(selected[0].opacity * 100)
                  : ""
              }
              onChange={(e) => {
                const opacity = e.target.valueAsNumber / 100;
                if (opacity >= 0.1 && opacity <= 1)
                  editor.updateShapes(
                    selected.map((s) => ({ id: s.id, type: s.type, opacity })),
                  );
              }}
            />
          </label>
        )}
      <TraceControls model={model} />

      {s && ["image", "document", "table"].includes(s.type) && (
        <label>
          Name
          <input
            aria-label="Object name"
            maxLength={120}
            value={
              s.type === "image"
                ? displayObjectName(s.meta.name || s.props.altText, "")
                : s.type === "document" || s.type === "table"
                  ? s.props.name
                  : ""
            }
            onChange={(e) =>
              s.type === "image"
                ? editor.updateShape({
                    id: s.id,
                    type: s.type,
                    meta: { ...s.meta, name: e.target.value },
                  })
                : (s.type === "document" || s.type === "table") &&
                  editor.updateShape({
                    id: s.id,
                    type: s.type,
                    props: { name: e.target.value },
                  })
            }
          />
        </label>
      )}
      {s?.type === "document" && !nativeDocument(s) && (
        <EditableDocumentControls model={model} shape={s} />
      )}
      {s?.type === "table" && <TableControls model={model} shape={s} />}
      {s?.type === "image" &&
        !(
          s.props.assetId &&
          editor.getAsset(s.props.assetId)?.meta.documentSource &&
          !editor.getAsset(s.props.assetId)?.meta.imageFile &&
          !s.meta.traceSnapshot
        ) && <ImageControls model={model} shape={s} />}
      {selected.every(
        (s) =>
          !["sheet", "mark", "trace"].includes(s.type) && !nativeDocument(s),
      ) && (
        <select
          aria-label="Move selection to sheet"
          value=""
          onChange={(e) => {
            const sheet = model.sheets.find((s) => s.id === e.target.value);
            if (sheet)
              run(() =>
                attachToSheet(editor, editor.getSelectedShapeIds(), sheet),
              );
          }}
        >
          <option value="">Move to sheet…</option>
          {model.sheets
            .filter((s) => !s.isLocked)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.props.name}
              </option>
            ))}
        </select>
      )}
      {s && isSource(s) && <SourceControls model={model} source={s} />}
      {s && model.canComment && (
        <button
          onClick={() => {
            const bounds = editor.getShapeGeometry(s).bounds;
            model.setPanel(null);
            model.setPending({ kind: "point", shape: s, p: bounds.center });
            model.setMessage(toRichText(""));
            model.setReview({ mentions: [] });
            model.setThreadId(null);
          }}
        >
          Comment
        </button>
      )}
    </div>
  );
}
